import React, { useState } from 'react'
import styled from 'styled-components'

// Components
import DialogScreen, { InnerContent } from '../DialogScreen'
import Text from '../Text'
import Dropdown from '../formControl/Dropdown'
import Textarea from '../formControl/Textarea'
import { SUPPORT_EMAIL } from '../../constants/api'
import { useAppDispatch, useAppState } from '../../context/AppContext'
import { SupportEmailCategories } from '../../common/models/Email'
import { useTranslation } from 'react-i18next'

interface IProps {
  closeModal: () => void
}

const SupportFormModal: React.FC<IProps> = ({ closeModal }) => {
  const appState = useAppState()
  const appDispatch = useAppDispatch()
  const authMember = appState.user?.asCompanyMember
  const { t } = useTranslation()

  const [formSent, setFormSent] = useState<boolean>(false)
  const [values, setValues] = useState<{ category: string; message: string }>({
    category: '',
    message: '',
  })

  const submitForm = async () => {
    const ticket = {
      supportCategory: values.category,
      supportDescription: values.message,
    }

    const requestOptions = {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + (appState.userAuthToken || ''),
      },
      body: JSON.stringify(ticket),
    }

    const response = await fetch(SUPPORT_EMAIL(authMember?._id), requestOptions)

    if (response.ok) {
      setFormSent(true)
    } else {
      const responseJson = await response.json()
      appDispatch({
        type: 'updateAlertPopup',
        payload: {
          title: t('supportFormModal.errorEmailSend'),
          content: responseJson.externalMessage,
        },
      })
    }
  }

  return (
    <StyledDialogScreen
      content={{
        title: formSent
          ? t('supportFormModal.emailSent')
          : t('supportFormModal.supportTitle'),
        subtitle: formSent
          ? t('general.thankYou')
          : t('supportFormModal.supportSubtitle'),
        content: (
          <React.Fragment>
            <Text textAlign="center" marginBottom={24}>
              {formSent
                ? t('supportFormModal.supportText')
                : t('supportFormModal.contactText')}
            </Text>
            {formSent && (
              <Text textStyle="caption" color="textLighter" textAlign="center">
                {t('supportFormModal.thanksText')}
              </Text>
            )}
            {!formSent && (
              <StyledDropdown
                coloredBackground
                label={t('supportFormModal.selectCategory')}
                handleChange={(e) => {
                  e.persist()
                  setValues((prevState) => ({
                    category: e.target.value,
                    message: prevState.message,
                  }))
                }}
              >
                {SupportEmailCategories.map((item) => (
                  <option key={item.key + '-dropdown-item'} value={item.key}>
                    {t(item.value)}
                  </option>
                ))}
              </StyledDropdown>
            )}
            {!formSent && (
              <Textarea
                onChange={(e) => {
                  e.persist()
                  setValues((prevState) => ({
                    category: prevState.category,
                    message: e.target.value,
                  }))
                }}
                label={t('supportFormModal.tellMore')}
                placeholder={t('supportFormModal.tellAnything')}
                size="xlarge"
              />
            )}
          </React.Fragment>
        ),
      }}
      dismissButton={!formSent ? t('general.actions.cancel') : undefined}
      actionButton={
        !formSent
          ? t('supportFormModal.sendEmail')
          : t('supportFormModal.closeReturn')
      }
      onSubmit={!formSent ? submitForm : closeModal}
      onDismiss={!formSent ? closeModal : undefined}
      actionButtonDisabled={
        !(values?.category !== '' && values?.message.length > 0)
      }
    />
  )
}

export default SupportFormModal

const StyledDropdown = styled(Dropdown)`
  margin-bottom: 24px;
`

const StyledDialogScreen = styled(DialogScreen)`
  ${InnerContent} {
    max-height: calc(80vh - 100px);
    padding-top: 8px;
    padding-bottom: 8px;
  }
`
